import React from "react";
import { Helmet } from "react-helmet-async";

/* ── Site-wide constants ── */
const SITE_NAME  = "Baby Twinkles Photography";
const SITE_URL   = "https://babytwinklesphotography.com";
const PHONE      = "+918074597649";
const EMAIL      = "Babytwinklespic1@gmail.com";
const ADDRESS    = "Avana Apartments, Road No. 47, Ayyappa Society, Madhapur, Hyderabad, Telangana 500081";
const LOGO_URL   = `${SITE_URL}/assets/img/sm-logo.svg`;
const OG_IMAGE   = `${SITE_URL}/assets/img/og-image.jpg`;
const INSTAGRAM  = "https://www.instagram.com/baby_twinkles_photography/";
const YOUTUBE    = "https://www.youtube.com/@babytwinklesphotography9753";
const MAPS_URL   = "https://www.google.com/maps/place/AVANA+APARTMENTS/@17.4490558,78.3926525,17z";
const GEO_LAT    = "17.4490558";
const GEO_LNG    = "78.3926525";

const SEOHelmet = ({
  title,
  description,
  keywords,
  canonicalUrl,
  schema,
  ogType   = "website",
  ogImage  = OG_IMAGE,
  noIndex  = false,
}) => {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} | Best Baby & Newborn Photographer in Hyderabad`;

  const schemaArray = Array.isArray(schema)
    ? schema
    : schema
    ? [schema]
    : [];

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: LOGO_URL,
    image: ogImage,
    telephone: PHONE,
    email: EMAIL,
    description:
      "Baby Twinkles Photography is Hyderabad's premier professional baby, newborn, and family portrait studio located in Madhapur. We specialize in newborn photography, cake smash sessions, milestone photography, maternity shoots, and themed baby portraits.",
    priceRange: "₹₹",
    currenciesAccepted: "INR",
    paymentAccepted: "Cash, UPI, Credit Card, Debit Card",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Avana Apartments, Road No. 47, Ayyappa Society",
      addressLocality: "Madhapur",
      addressRegion: "Telangana",
      postalCode: "500081",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: GEO_LAT,
      longitude: GEO_LNG,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"
        ],
        opens: "09:00",
        closes: "20:00",
      },
    ],
    sameAs: [INSTAGRAM, YOUTUBE, MAPS_URL],
    hasMap: MAPS_URL,
    areaServed: [
      {
        "@type": "City",
        name: "Hyderabad",
      },
      {
        "@type": "State",
        name: "Telangana",
      },
    ],
    serviceType: [
      "Newborn Photography",
      "Baby Photography",
      "Cake Smash Photography",
      "Milestone Photography",
      "Maternity Photography",
      "Family Portrait Photography",
      "Themed Baby Photoshoots",
    ],
  };

  const allSchemas = [orgSchema, ...schemaArray];

  return (
    <Helmet>
      {/* ── Core ── */}
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {!noIndex && (
        <meta
          name="robots"
          content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"
        />
      )}

      <meta name="geo.region"   content="IN-TG" />
      <meta name="geo.placename" content="Hyderabad, Telangana" />
      <meta name="geo.position" content={`${GEO_LAT};${GEO_LNG}`} />
      <meta name="ICBM"         content={`${GEO_LAT}, ${GEO_LNG}`} />

      <meta name="author"    content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="copyright" content={`© ${new Date().getFullYear()} ${SITE_NAME}`} />
      <meta name="revisit-after" content="7 days" />
      <meta name="language"   content="English" />
      <meta name="rating"     content="General" />

      <meta property="og:type"        content={ogType} />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url"         content={canonicalUrl || SITE_URL} />
      <meta property="og:site_name"   content={SITE_NAME} />
      <meta property="og:image"       content={ogImage} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt"    content={`${SITE_NAME} – Baby Photography Hyderabad`} />
      <meta property="og:locale"      content="en_IN" />

      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={ogImage} />
      <meta name="twitter:image:alt"   content={`${SITE_NAME} – Baby Photography Hyderabad`} />
      <meta name="twitter:site"        content="@babytwinklesphotography" />
      <meta name="twitter:creator"     content="@babytwinklesphotography" />

      {allSchemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHelmet;

export const siteUrl   = SITE_URL;
export const siteName  = SITE_NAME;
export const sitePhone = PHONE;
export const siteEmail = EMAIL;
export const siteAddress = ADDRESS;

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/gallery?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

export const buildBreadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const buildFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const buildServiceSchema = (name, description, url) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  url,
  provider: {
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
  },
  areaServed: {
    "@type": "City",
    name: "Hyderabad",
  },
});

export const gallerySchema = {
  "@context": "https://schema.org",
  "@type": "ImageGallery",
  name: `${SITE_NAME} – Baby & Newborn Photography Gallery`,
  description:
    "Browse our beautiful collection of newborn, baby, milestone, cake smash, and family photography from Hyderabad.",
  url: `${SITE_URL}/gallery`,
  author: {
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#organization`,
  },
};

export const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: `Contact ${SITE_NAME}`,
  description:
    "Get in touch with Baby Twinkles Photography to book your newborn, baby, or family photoshoot in Hyderabad, Telangana.",
  url: `${SITE_URL}/contact`,
  mainEntity: {
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#organization`,
  },
};

export const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: `About ${SITE_NAME}`,
  description:
    "Learn about Baby Twinkles Photography – Hyderabad's top-rated baby and newborn photography studio in Madhapur, Telangana.",
  url: `${SITE_URL}/about`,
  mainEntity: {
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#organization`,
  },
};

export const blogListSchema = {
  "@context": "https://schema.org",
  "@type": "Blog",
  name: `${SITE_NAME} – Photography Blog`,
  description:
    "Tips, inspiration, and stories about baby photography, newborn sessions, and family portraits from Baby Twinkles Photography, Hyderabad.",
  url: `${SITE_URL}/blogs`,
  author: {
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#organization`,
  },
};
